import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  InputBase,
  IconButton,
  Chip,
  Divider,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  TextField,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  MarkunreadMailbox as UnreadIcon,
  AttachFile as AttachIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Description as FileIcon,
  Send as SendIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { appealService } from '../services/appealService';
import { ticketService } from '../services/questService';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchAppeals,
  fetchAppealsSilent,
  fetchAppealDetail,
  fetchAccountBalance,
  fetchStatuses,
  fetchCategories,
  clearAccountBalance,
  setActiveStatusTab,
  sendMessage,
  uploadAttachment,
  markMessagesViewed,
  updateAppealStatus,
} from '../store/slices/appealsSlice';
import CreateQuestForm from '../components/quests/CreateQuestForm';
import AppealAttachments, { LocalFileThumb } from '../components/appeals/AppealAttachments';
import { getQuestStatusIndex, getStatusLabel } from '../utils/questStatus';
import { isRasterImageName, revokeTicketPreviewUrls } from '../utils/attachmentPreview';

const APPEALS_SILENT_REFRESH_MS = 25000;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

const isRealDomokeyDate = (value) => {
  if (!value) return false;
  const str = String(value);
  if (str.startsWith('0001-') || str.startsWith('1970-01-01')) return false;
  return !Number.isNaN(new Date(str).getTime());
};

const formatTimeHm = (d) => d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

const formatDayTime = (d) =>
  `${d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeHm(d)}`;

const formatCallInterval = (from, to) => {
  if (!isRealDomokeyDate(from) || !isRealDomokeyDate(to)) return null;
  const fromDate = new Date(from);
  const toDate = new Date(to);
  if (fromDate.getTime() === toDate.getTime()) return formatDayTime(fromDate);
  if (fromDate.toDateString() === toDate.toDateString()) {
    return `${formatDayTime(fromDate)} — ${formatTimeHm(toDate)}`;
  }
  return `${formatDayTime(fromDate)} — ${formatDayTime(toDate)}`;
};

const formatCurrency = (value) => new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
}).format(value);

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const truncate = (str, maxLen = 80) => {
  if (!str) return '';
  return str.length > maxLen ? str.slice(0, maxLen) + '...' : str;
};

const getStatusType = (status) => status?.Type?.trim().toLowerCase();

const getAppealAccountId = (appeal) => (
  appeal?.AccountId ||
  appeal?.AccountID ||
  appeal?.AccountGuid ||
  appeal?.PersonalAccountId ||
  appeal?.PersonalAccountGuid ||
  appeal?.accountId ||
  appeal?.accountID ||
  appeal?.accountGuid ||
  appeal?.personalAccountId ||
  appeal?.personalAccountGuid ||
  appeal?.Account?.Id ||
  appeal?.Account?.id ||
  appeal?.Account?.ID ||
  appeal?.Account?.AccountId ||
  appeal?.Account?.accountId ||
  appeal?.Account?.AccountGuid ||
  appeal?.Account?.accountGuid ||
  appeal?.PersonalAccount?.Id ||
  appeal?.PersonalAccount?.id ||
  appeal?.PersonalAccount?.AccountId ||
  appeal?.PersonalAccount?.accountId ||
  appeal?.personalAccount?.id ||
  appeal?.personalAccount?.accountId ||
  appeal?.account?.id ||
  appeal?.account?.Id ||
  appeal?.account?.accountId ||
  appeal?.account?.AccountId ||
  ''
);

const getRelatedQuestStatusLabel = (quest) => {
  const explicitLabel =
    quest?.questStatusName ||
    quest?.QuestStatusName ||
    quest?.statusName ||
    quest?.StatusName ||
    quest?.status?.name ||
    quest?.status?.Name ||
    quest?.Status?.name ||
    quest?.Status?.Name;

  if (explicitLabel) return explicitLabel;

  const rawStatus = quest?.status ?? quest?.statusId ?? quest?.Status ?? quest?.StatusId;
  if (rawStatus == null || rawStatus === '') return '';

  if (typeof rawStatus === 'object') {
    const nestedStatus = rawStatus.status ?? rawStatus.statusId ?? rawStatus.id ?? rawStatus.Id ?? rawStatus.code ?? rawStatus.Code;
    return nestedStatus == null || nestedStatus === '' ? '' : getStatusLabel(nestedStatus);
  }

  return getStatusLabel(rawStatus);
};

const getRelatedQuestId = (quest) =>
  quest?.questId || quest?.QuestId || quest?.id || quest?.Id || '';

const getRelatedQuestUrl = (quest) => {
  const questId = getRelatedQuestId(quest);
  if (!questId) return null;

  const params = new URLSearchParams({ questId: String(questId) });
  params.set('questTab', String(getQuestStatusIndex(quest)));
  return `/?${params.toString()}`;
};

const normalizeRelatedQuest = (quest) => {
  if (!quest) return null;

  const questId = quest.questId || quest.QuestId || quest.id || quest.Id;
  if (!questId) return null;

  return {
    ...quest,
    questId,
    number: quest.number || quest.Number || quest.questNumber || quest.QuestNumber || questId.slice(0, 8),
    title: quest.title || quest.Title || quest.name || quest.Name || 'Без названия',
  };
};

const addRelatedQuest = (quests = [], quest) => {
  const normalizedQuest = normalizeRelatedQuest(quest);
  if (!normalizedQuest) return quests;

  const exists = quests.some(q => (q.questId || q.QuestId || q.id || q.Id) === normalizedQuest.questId);
  return exists
    ? quests.map(q => ((q.questId || q.QuestId || q.id || q.Id) === normalizedQuest.questId ? normalizedQuest : q))
    : [...quests, normalizedQuest];
};

const AppealListItem = ({ appeal, isSelected, onClick, hasQuests, itemRef }) => {
  const theme = useTheme();

  return (
    <Box
      ref={itemRef}
      onClick={onClick}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'all 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          #{appeal.Number || appeal.Id?.slice(0, 8)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {hasQuests && (
            <AssignmentIcon sx={{ fontSize: 16, color: 'primary.main' }} titleAccess="Есть заявка" />
          )}
          {appeal.HasUnreadMessages && (
            <UnreadIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          )}
          {appeal.HasAttachments && (
            <AttachIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          )}
        </Box>
      </Box>

      {appeal.Category?.Name && (
        <Chip
          label={appeal.Category.Name}
          size="small"
          variant="outlined"
          sx={{ mb: 0.5, height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 1 } }}
        />
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.3 }}>
        {truncate(appeal.FirstMessage)}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {appeal.OwnerName || 'Не указан'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(appeal.CreatedAt)}
        </Typography>
      </Box>
    </Box>
  );
};

const MessageInput = ({ ticketId, user }) => {
  const dispatch = useDispatch();
  const { sendingMessage, uploadingAttachment } = useSelector((state) => state.appeals);
  const [text, setText] = React.useState('');
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const fileInputRef = React.useRef(null);

  const isSending = sendingMessage || uploadingAttachment;

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed && selectedFiles.length === 0) return;

    const messageAuthorName = user?.name || '';
    const messageAuthorId = user?.id || user?.sub || '';

    try {
      if (trimmed) {
        await dispatch(sendMessage({
          ticketId,
          messageData: { MessageText: trimmed, AuthorName: messageAuthorName, AuthorId: messageAuthorId },
        })).unwrap();
        setText('');
      }

      const attachmentProfile = selectedFiles.length > 0
        ? await appealService.getProfile()
        : null;
      const attachmentAuthorName = attachmentProfile?.name || '';

      for (const file of selectedFiles) {
        await dispatch(uploadAttachment({
          ticketId,
          file,
          authorName: attachmentAuthorName,
          authorId: '',
        })).unwrap();
      }
      setSelectedFiles([]);

      dispatch(fetchAppeals());
    } catch {
      dispatch(fetchAppeals());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isSending) {
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      {selectedFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1, alignItems: 'center' }}>
          {selectedFiles.map((file, index) => (
            isRasterImageName(file.name) ? (
              <LocalFileThumb
                key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                file={file}
                onRemove={() => handleRemoveFile(index)}
              />
            ) : (
              <Chip
                key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                icon={<AttachIcon sx={{ fontSize: 16 }} />}
                label={`${file.name} (${formatFileSize(file.size)})`}
                onDelete={() => handleRemoveFile(index)}
                size="small"
                variant="outlined"
                sx={{ maxWidth: 250 }}
              />
            )
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <TextField
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Введите сообщение... (Ctrl+Enter для отправки)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSending}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              bgcolor: '#fff',
            },
          }}
        />

        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleAttachClick}
            disabled={isSending}
            title="Прикрепить файл"
          >
            <CloudUploadIcon />
          </IconButton>

          <Button
            variant="contained"
            size="small"
            onClick={handleSend}
            disabled={isSending || (!text.trim() && selectedFiles.length === 0)}
            sx={{ minWidth: 0, px: 1.5 }}
          >
            {isSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const CategoryChanger = ({ appeal }) => {
  const dispatch = useDispatch();
  const anchorRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [changing, setChanging] = React.useState(false);
  const { categories, categoriesLoading } = useSelector((state) => state.appeals);

  const handleOpen = () => {
    setOpen(true);
    if (categories.length === 0) {
      dispatch(fetchCategories());
    }
  };

  const handleClose = () => setOpen(false);

  const handleSelect = async (categoryId) => {
    if (categoryId === appeal.Category?.Id) {
      setOpen(false);
      return;
    }
    setChanging(true);
    try {
      await dispatch(updateAppealStatus({
        ticketId: appeal.Id,
        data: { categoryId },
      })).unwrap();
      dispatch(fetchAppeals());
    } catch {}
    setChanging(false);
    setOpen(false);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
        {appeal.Category?.Name && (
          <Chip label={appeal.Category.Name} color="primary" size="small" />
        )}
        <IconButton
          ref={anchorRef}
          size="small"
          onClick={handleOpen}
          disabled={changing}
          sx={{ p: 0.25 }}
        >
          {changing ? <CircularProgress size={16} /> : <MoreVertIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { maxHeight: 320, width: 280 },
        }}
      >
        {categoriesLoading && (
          <MenuItem disabled>
            <CircularProgress size={18} sx={{ mr: 1 }} />
            <ListItemText>Загрузка...</ListItemText>
          </MenuItem>
        )}
        {!categoriesLoading && categories.length === 0 && (
          <MenuItem disabled>
            <ListItemText>Нет категорий</ListItemText>
          </MenuItem>
        )}
        {categories.map((cat) => (
          <MenuItem
            key={cat.Id}
            onClick={() => handleSelect(cat.Id)}
            selected={cat.Id === appeal.Category?.Id}
          >
            <ListItemText>{cat.Name}</ListItemText>
            {cat.Id === appeal.Category?.Id && (
              <ListItemIcon sx={{ minWidth: 'auto', ml: 1 }}>
                <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const AppealDetail = ({ appeal, loading, user, statuses, onCancel, onCreateTask, onTakeToWork, quests }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountBalance, accountBalanceLoading, accountBalanceError } = useSelector((state) => state.appeals);
  const viewedSentRef = React.useRef(null);
  const [commentText, setCommentText] = React.useState('');
  const [commentDirty, setCommentDirty] = React.useState(false);
  const [commentSaving, setCommentSaving] = React.useState(false);

  React.useEffect(() => {
    setCommentText(appeal?.Comment || '');
    setCommentDirty(false);
  }, [appeal?.Id, appeal?.Comment]);

  const handleSaveComment = async () => {
    if (!commentDirty) return;
    setCommentSaving(true);
    try {
      await dispatch(updateAppealStatus({
        ticketId: appeal.Id,
        data: { comment: commentText },
      })).unwrap();
      setCommentDirty(false);
      dispatch(fetchAppeals());
    } catch {}
    setCommentSaving(false);
  };

  const currentStatus = statuses?.find(s => s.Id === appeal?.StatusId);
  console.log('[AppealDetail] currentStatus:', currentStatus, 'all statuses:', statuses);

  const currentStatusType = getStatusType(currentStatus);
  const isClosed = currentStatusType === 'closed';
  const isCanceled = currentStatusType === 'canceled';
  const hasClosedAt = Boolean(appeal?.ClosedAt && !String(appeal.ClosedAt).startsWith('0001-'));
  const canReply = !isClosed && !isCanceled && !hasClosedAt;
  const isCreated = currentStatusType === 'created';
  const accountId = getAppealAccountId(appeal);
  const accountBalanceValue = Number(accountBalance?.balance ?? accountBalance?.Balance);
  const accountBalanceLabel = accountBalanceValue < 0 ? 'Переплата' : 'Задолженность';
  const accountBalanceText = accountBalanceLoading
    ? 'загрузка...'
    : accountBalanceError
      ? 'не удалось загрузить'
      : Number.isFinite(accountBalanceValue)
        ? formatCurrency(Math.abs(accountBalanceValue))
        : '';
  const accountInfo = [
    appeal?.AccountAddress && { label: 'Адрес', value: appeal.AccountAddress },
    appeal?.AccountNumber && { label: 'ЛС', value: appeal.AccountNumber },
    accountId && accountBalanceText && { label: accountBalanceLabel, value: accountBalanceText },
  ].filter(Boolean);
  const callInterval = formatCallInterval(appeal?.IntervalToCallFrom, appeal?.IntervalToCallTo);

  React.useEffect(() => {
    if (!accountId) {
      dispatch(clearAccountBalance());
      if (appeal?.Id) {
        console.warn('[AppealDetail] account id not found for balance request', {
          ticketId: appeal.Id,
          fields: Object.keys(appeal),
          accountNumber: appeal.AccountNumber,
        });
      }
      return;
    }

    dispatch(fetchAccountBalance(accountId));
  }, [accountId, appeal, dispatch]);

  React.useEffect(() => {
    if (!appeal?.Id) return undefined;
    const ticketId = appeal.Id;
    return () => revokeTicketPreviewUrls(ticketId);
  }, [appeal?.Id]);

  React.useEffect(() => {
    if (!appeal?.Messages) return;

    const unreadIds = appeal.Messages
      .filter((m) => m.MessageType !== 'outcoming' && !m.Viewed)
      .map((m) => m.Id);

    if (unreadIds.length === 0) return;

    if (viewedSentRef.current === appeal.Id) return;
    viewedSentRef.current = appeal.Id;

    const timer = setTimeout(() => {
      const employee = user?.id || user?.sub || '';
      dispatch(markMessagesViewed({
        ticketId: appeal.Id,
        messageIds: unreadIds,
        employee,
      }));
      dispatch(fetchAppeals());
    }, 3000);

    return () => clearTimeout(timer);
  }, [appeal?.Id, appeal?.Messages, dispatch, user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!appeal) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h6" color="text.secondary">
          Выберите обращение
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Обращение #{appeal.Number || appeal.Id?.slice(0, 8)}
          </Typography>
          {(isClosed || hasClosedAt) && (
            <LockIcon sx={{ fontSize: 22, color: 'text.secondary' }} titleAccess="Завершено" />
          )}
          {!isClosed ? (
            <CategoryChanger appeal={appeal} />
          ) : (
            appeal.Category?.Name && (
              <Chip label={appeal.Category.Name} color="primary" size="small" sx={{ ml: 'auto' }} />
            )
          )}
        </Box>

        {accountInfo.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
            {accountInfo.map(({ label, value }) => (
              <Typography key={label} variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>{label}: </Box>
                {value}
              </Typography>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Создано: {formatDate(appeal.CreatedAt)}
            {appeal.LastModifiedAt && ` | Обновлено: ${formatDate(appeal.LastModifiedAt)}`}
            {hasClosedAt && ` | Завершено: ${formatDate(appeal.ClosedAt)}`}
          </Typography>
          {!isClosed && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              {isCreated && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onTakeToWork?.(appeal)}
                  sx={{ textTransform: 'none' }}
                >
                  Принять в работу
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => onCreateTask?.(appeal)}
                sx={{ textTransform: 'none' }}
              >
                Создать заявку
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => onCancel?.(appeal)}
                sx={{ textTransform: 'none' }}
              >
                Завершить
              </Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Info grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3 }}>
          {appeal.OwnerName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Владелец</Typography>
                <Typography variant="body2">{appeal.OwnerName}</Typography>
              </Box>
            </Box>
          )}
          {appeal.PhoneNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Телефон</Typography>
                <Typography variant="body2">{appeal.PhoneNumber}</Typography>
              </Box>
            </Box>
          )}
          {callInterval && (
            <Box>
              <Typography variant="caption" color="text.secondary">Время для звонка</Typography>
              <Typography variant="body2">{callInterval}</Typography>
            </Box>
          )}
          {appeal.InServiceAt && !appeal.InServiceAt.startsWith('0001-') && (
            <Box>
              <Typography variant="caption" color="text.secondary">В работе с</Typography>
              <Typography variant="body2">{formatDate(appeal.InServiceAt)}</Typography>
            </Box>
          )}
        </Box>

        {/* Related quests */}
        {quests && quests.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AssignmentIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography variant="subtitle2">Связанные заявки ({quests.length})</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {quests.map((q) => {
                const statusLabel = getRelatedQuestStatusLabel(q);
                const openQuest = () => {
                  const url = getRelatedQuestUrl(q);
                  if (url) navigate(url);
                };

                return (
                  <Box
                    key={getRelatedQuestId(q) || q.number}
                    onClick={openQuest}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Заявка №{q.number}{statusLabel ? ` [${statusLabel}]` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {q.title}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      title="Открыть заявку"
                      aria-label="Открыть заявку"
                      onClick={(event) => {
                        event.stopPropagation();
                        openQuest();
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2">Комментарий</Typography>
            {commentDirty && (
              <Button
                variant="text"
                size="small"
                onClick={handleSaveComment}
                disabled={commentSaving}
                startIcon={commentSaving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', minWidth: 0, py: 0, px: 1 }}
              >
                Сохранить
              </Button>
            )}
          </Box>
          <TextField
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            placeholder="Введите комментарий..."
            value={commentText}
            onChange={(e) => { setCommentText(e.target.value); setCommentDirty(true); }}
            disabled={commentSaving}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                bgcolor: 'grey.50',
              },
            }}
          />
        </Box>

        {/* Messages with inline attachments */}
        {((appeal.Messages?.length || 0) > 0 || (appeal.Attachments?.length || 0) > 0) && (() => {
          const messages = appeal.Messages || [];
          const messageIds = new Set(messages.map((msg) => msg.Id).filter(Boolean));
          const attachmentsByMsg = new Map();
          const orphanAttachments = [];
          (appeal.Attachments || []).forEach((att) => {
            const messageId = att.MessageId;
            if (messageId && messageIds.has(messageId)) {
              const list = attachmentsByMsg.get(messageId) || [];
              list.push(att);
              attachmentsByMsg.set(messageId, list);
            } else {
              orphanAttachments.push(att);
            }
          });

          return (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MessageIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2">Сообщения ({messages.length})</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...messages]
                  .sort((a, b) => (a.OrderNumber || 0) - (b.OrderNumber || 0))
                  .map((msg) => {
                    const isIncoming = msg.MessageType === 'incoming';
                    const isUnread = msg.MessageType !== 'outcoming' && !msg.Viewed;
                    const msgAttachments = attachmentsByMsg.get(msg.Id) || [];
                    const messageText = msg.MessageText || '';
                    const displayMessageText = messageText.trim() || msgAttachments.length === 0
                      ? messageText
                      : 'Добавлено вложение';

                    return (
                      <Box
                        key={msg.Id}
                        sx={{
                          display: 'flex',
                          justifyContent: isIncoming ? 'flex-start' : 'flex-end',
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '75%',
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: isIncoming
                              ? (isUnread ? '#e3f2fd' : 'grey.100')
                              : (isUnread ? '#fff3e0' : '#e8f5e9'),
                            borderLeft: isIncoming && isUnread ? '3px solid' : 'none',
                            borderRight: !isIncoming && isUnread ? '3px solid' : 'none',
                            borderColor: isIncoming ? 'primary.main' : 'warning.main',
                            position: 'relative',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {msg.AuthorName || (isIncoming ? 'Жилец' : 'Диспетчер')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(msg.CreatedAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {displayMessageText}
                          </Typography>
                          {msgAttachments.length > 0 && (
                            <AppealAttachments ticketId={appeal.Id} attachments={msgAttachments} />
                          )}
                          {isUnread && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: isIncoming ? 8 : 'auto',
                                left: !isIncoming ? 8 : 'auto',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: isIncoming ? 'primary.main' : 'warning.main',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                {/* Orphan attachments without a matching message */}
                {orphanAttachments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FileIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography variant="subtitle2">Вложения ({orphanAttachments.length})</Typography>
                    </Box>
                    <AppealAttachments ticketId={appeal.Id} attachments={orphanAttachments} />
                  </Box>
                )}
              </Box>
            </Box>
          );
        })()}
      </Box>

      {canReply && <MessageInput ticketId={appeal.Id} user={user} />}
    </Box>
  );
};

const AppealsPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const { appeals, currentAppeal, loading, detailLoading, error, statuses, activeStatusTab } = useSelector(
    (state) => state.appeals
  );
  const user = useSelector((state) => state.auth.user);

  const [selectedId, setSelectedId] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [notification, setNotification] = React.useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [createTicketOpen, setCreateTicketOpen] = React.useState(false);
  const [ticketInitialData, setTicketInitialData] = React.useState(null);
  const [questMap, setQuestMap] = React.useState({});
  const questMapRef = React.useRef(questMap);
  questMapRef.current = questMap;
  const [selectedQuests, setSelectedQuests] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState('desc'); // 'desc' - новые сверху, 'asc' - старые сверху
  const handledAppealLinkRef = React.useRef(null);
  const selectedItemRef = React.useRef(null);
  const scrolledForIdRef = React.useRef(null);

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  React.useEffect(() => {
    console.log('[AppealsPage] user:', user);
  }, [user]);

  const showNotification = (message, severity = 'info') =>
    setNotification({ open: true, message, severity });

  const loadAppealDetails = React.useCallback((appealId) => {
    setSelectedId(appealId);
    setSelectedQuests(null);

    return dispatch(fetchAppealDetail({ ticketId: appealId }))
      .unwrap()
      .then(() =>
        ticketService.getQuestsByTicket(appealId)
          .then((data) => setSelectedQuests(data?.quests || []))
          .catch(() => setSelectedQuests([]))
      );
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(fetchAppeals())
      .unwrap()
      .catch((err) => showNotification(`Ошибка загрузки: ${err}`, 'error'));
  }, [dispatch]);

  React.useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const poll = () => {
      if (cancelled || inFlight || document.hidden) return;
      inFlight = true;
      dispatch(fetchAppealsSilent()).finally(() => {
        inFlight = false;
      });
    };

    const intervalId = setInterval(poll, APPEALS_SILENT_REFRESH_MS);
    const onVisibilityChange = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch]);

  React.useEffect(() => {
    dispatch(fetchStatuses())
      .unwrap()
      .catch((err) => showNotification(`Ошибка загрузки статусов: ${err}`, 'error'));
  }, [dispatch]);

  React.useEffect(() => {
    const list = appeals || [];
    const idSet = new Set(list.map((a) => a.Id));
    let cancelled = false;

    setQuestMap((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (idSet.has(key)) next[key] = prev[key];
      });
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (nextKeys.length === prevKeys.length && nextKeys.every((key) => prev[key] === next[key])) {
        return prev;
      }
      return next;
    });

    const missing = list.filter((a) => a.Id && questMapRef.current[a.Id] === undefined);
    if (missing.length === 0) return undefined;

    const fetchQuests = async () => {
      const results = {};
      await Promise.all(
        missing.map(async (a) => {
          try {
            const data = await ticketService.getQuestsByTicket(a.Id);
            if (!cancelled) results[a.Id] = data;
          } catch {
            // ignore — appeal has no quests or API error
          }
        })
      );
      if (!cancelled && Object.keys(results).length > 0) {
        setQuestMap((prev) => ({ ...prev, ...results }));
      }
    };
    fetchQuests();
    return () => { cancelled = true; };
  }, [appeals]);

  React.useEffect(() => {
    const appealId = searchParams.get('appealId');
    if (!appealId) return;

    const linkKey = appealId;
    if (handledAppealLinkRef.current === linkKey) return;
    handledAppealLinkRef.current = linkKey;

    setSearchQuery('');
    dispatch(setActiveStatusTab('all'));
    loadAppealDetails(appealId)
      .catch((err) => showNotification(`Ошибка загрузки: ${err}`, 'error'));
  }, [searchParams, dispatch, loadAppealDetails]);

  const handleSelectAppeal = (appeal) => {
    if (!appeal?.Id) return;
    handledAppealLinkRef.current = appeal.Id;
    if (searchParams.get('appealId') !== appeal.Id) {
      const next = new URLSearchParams(searchParams);
      next.set('appealId', appeal.Id);
      setSearchParams(next, { replace: true });
    }
    loadAppealDetails(appeal.Id)
      .catch((err) => showNotification(`Ошибка загрузки: ${err}`, 'error'));
  };

  const handleCreateTask = (appeal) => {
    setTicketInitialData({
      clientName: appeal.OwnerName || '',
      phoneNumber: appeal.PhoneNumber || '',
      accountNumber: appeal.AccountNumber || '',
      relatedTicketId: appeal.Id,
    });
    setCreateTicketOpen(true);
  };

  const handleTakeToWork = async (appeal) => {
    const processingStatus = statuses?.find(s => getStatusType(s) === 'processing');
    if (!processingStatus) {
      showNotification('Не найден статус "В работе"', 'error');
      return;
    }
    try {
      await dispatch(updateAppealStatus({
        ticketId: appeal.Id,
        data: {
          statusId: processingStatus.Id,
          categoryId: appeal.Category?.Id,
        },
      })).unwrap();
      showNotification('Обращение принято в работу', 'success');
      dispatch(fetchAppeals());
    } catch (err) {
      showNotification(`Ошибка: ${err}`, 'error');
    }
  };

  const handleCompleteAppeal = async (appeal) => {
    const closedStatus = statuses?.find(s => getStatusType(s) === 'closed');
    if (!closedStatus) {
      showNotification('Не найден статус "Завершено"', 'error');
      return;
    }
    try {
      await dispatch(updateAppealStatus({
        ticketId: appeal.Id,
        data: {
          statusId: closedStatus.Id,
          categoryId: appeal.Category?.Id,
        },
      })).unwrap();
      showNotification('Обращение завершено', 'success');
      dispatch(fetchAppeals());
    } catch (err) {
      showNotification(`Ошибка: ${err}`, 'error');
    }
  };

  const handleCancelAppeal = async (appeal) => {
    if (!window.confirm('Вы уверены, что хотите завершить обращение?')) return;
    await handleCompleteAppeal(appeal);
  };

  const handleCloseCreateTicket = () => {
    setCreateTicketOpen(false);
    setTicketInitialData(null);
  };

  const handleQuestCreatedFromAppeal = async (createdQuest) => {
    showNotification('Заявка создана из обращения', 'success');

    const relatedTicketId = ticketInitialData?.relatedTicketId;
    if (!relatedTicketId) return;

    const normalizedQuest = normalizeRelatedQuest(createdQuest);

    if (normalizedQuest) {
      setQuestMap(prev => {
        const currentData = prev[relatedTicketId] || {};
        const updatedQuests = addRelatedQuest(currentData.quests || [], normalizedQuest);
        return {
          ...prev,
          [relatedTicketId]: {
            ...currentData,
            quests: updatedQuests,
            count: updatedQuests.length,
          },
        };
      });

      if (selectedId === relatedTicketId) {
        setSelectedQuests(prev => addRelatedQuest(prev || [], normalizedQuest));
      }

      return;
    }

    try {
      const data = await ticketService.getQuestsByTicket(relatedTicketId);
      setQuestMap(prev => ({ ...prev, [relatedTicketId]: data }));
      if (selectedId === relatedTicketId) {
        setSelectedQuests(data?.quests || []);
      }
    } catch {
      // Уведомление об успехе создания уже показано; связь обновится при следующей загрузке.
    }
  };

  const statusCounts = React.useMemo(() => {
    const counts = { all: appeals?.length || 0 };
    if (appeals && appeals.length > 0) {
      appeals.forEach((a) => {
        if (a.StatusId) {
          counts[a.StatusId] = (counts[a.StatusId] || 0) + 1;
        }
      });
    }
    return counts;
  }, [appeals]);

  const filteredAppeals = React.useMemo(() => {
    if (!appeals || appeals.length === 0) return [];
    let result = appeals;

    if (activeStatusTab !== 'all') {
      result = result.filter((a) => a.StatusId === activeStatusTab);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          (a.FirstMessage && a.FirstMessage.toLowerCase().includes(q)) ||
          (a.OwnerName && a.OwnerName.toLowerCase().includes(q)) ||
          (a.Category?.Name && a.Category.Name.toLowerCase().includes(q)) ||
          String(a.Number).includes(q)
      );
    }

    // Сортировка по дате создания
    const sorted = [...result].sort((a, b) => {
      const dateA = new Date(a.CreatedAt || 0);
      const dateB = new Date(b.CreatedAt || 0);
      if (sortOrder === 'desc') {
        return dateB - dateA; // новые сверху
      } else {
        return dateA - dateB; // старые сверху
      }
    });
    return sorted;
  }, [appeals, activeStatusTab, searchQuery, sortOrder]);

  React.useEffect(() => {
    if (!selectedId || scrolledForIdRef.current === selectedId) return;
    if (!selectedItemRef.current) return;
    selectedItemRef.current.scrollIntoView({ block: 'nearest' });
    scrolledForIdRef.current = selectedId;
  }, [selectedId, filteredAppeals]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'auto' : 'calc(100vh - 132px)',
        overflow: 'hidden',
      }}
    >
      {error && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{error}</Alert>}

      {/* Status tabs */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '2px solid',
          borderColor: 'divider',
          px: 2,
          gap: 0,
        }}
      >
        <Box
          onClick={() => dispatch(setActiveStatusTab('all'))}
          sx={{
            px: 3,
            py: 1.5,
            cursor: 'pointer',
            position: 'relative',
            whiteSpace: 'nowrap',
            fontSize: '0.9rem',
            fontWeight: activeStatusTab === 'all' ? 600 : 400,
            color: activeStatusTab === 'all' ? 'primary.main' : 'text.secondary',
            borderBottom: activeStatusTab === 'all' ? '3px solid' : '3px solid transparent',
            borderColor: activeStatusTab === 'all' ? 'primary.main' : 'transparent',
            mb: '-2px',
            transition: 'all 0.2s',
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          Все{' '}
          <Box
            component="span"
            sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '0.85em' }}
          >
            ({statusCounts.all})
          </Box>
        </Box>

        {statuses.map((status) => (
          <Box
            key={status.Id}
            onClick={() => dispatch(setActiveStatusTab(status.Id))}
            sx={{
              px: 3,
              py: 1.5,
              cursor: 'pointer',
              position: 'relative',
              whiteSpace: 'nowrap',
              fontSize: '0.9rem',
              fontWeight: activeStatusTab === status.Id ? 600 : 400,
              color: activeStatusTab === status.Id ? 'primary.main' : 'text.secondary',
              borderBottom: activeStatusTab === status.Id ? '3px solid' : '3px solid transparent',
              borderColor: activeStatusTab === status.Id ? 'primary.main' : 'transparent',
              mb: '-2px',
              transition: 'all 0.2s',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            {status.Name}{' '}
            <Box
              component="span"
              sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '0.85em' }}
            >
              ({statusCounts[status.Id] || 0})
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Left panel — list */}
        <Box
          sx={{
            width: isMobile ? '100%' : 360,
            minWidth: isMobile ? '100%' : 320,
            maxWidth: isMobile ? '100%' : 400,
            borderRight: isMobile ? 'none' : '1px solid',
            borderBottom: isMobile ? '1px solid' : 'none',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            height: isMobile ? 350 : '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Search & Sort */}
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tooltip title={sortOrder === 'desc' ? 'Сначала новые' : 'Сначала старые'}>
              <IconButton
                size="small"
                onClick={toggleSortOrder}
                sx={{
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  p: 0.75,
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                {sortOrder === 'desc' ? (
                  <ArrowDownwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                ) : (
                  <ArrowUpwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                )}
              </IconButton>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'grey.100', borderRadius: 1, px: 1.5, py: 0.5, flex: 1 }}>
              <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
              <InputBase
                placeholder="Поиск обращений..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, fontSize: '0.875rem' }}
              />
              {searchQuery && (
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : filteredAppeals.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? 'Ничего не найдено' : 'Нет обращений'}
                </Typography>
              </Box>
            ) : (
              filteredAppeals.map((appeal) => (
                <Box key={appeal.Id}>
                  <AppealListItem
                    appeal={appeal}
                    isSelected={selectedId === appeal.Id}
                    onClick={() => handleSelectAppeal(appeal)}
                    hasQuests={questMap[appeal.Id]?.count > 0}
                    itemRef={selectedId === appeal.Id ? selectedItemRef : undefined}
                  />
                  <Divider />
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Right panel — detail */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            bgcolor: '#fff',
            height: isMobile ? 'auto' : '100%',
            minHeight: isMobile ? 400 : 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AppealDetail
            appeal={currentAppeal}
            loading={detailLoading}
            user={user}
            statuses={statuses}
            onCancel={handleCancelAppeal}
            onCreateTask={handleCreateTask}
            onTakeToWork={handleTakeToWork}
            quests={selectedQuests}
          />
        </Box>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <CreateQuestForm
        open={createTicketOpen}
        onClose={handleCloseCreateTicket}
        onQuestCreated={handleQuestCreatedFromAppeal}
        initialData={ticketInitialData}
      />
    </Box>
  );
};

export default AppealsPage;
